import { TicketCheckInStatus } from "../../models/enum/ticketCheckInStatus";
import { twclsx } from "../../utils/cssUtil";
import { useTranslation } from "react-i18next";
import { Button } from "../common/button";
import { useCheckIn } from "../../hooks/useCheckIn";
import { Dialog, DialogActions, DialogMessage } from "../common/dialog";
import { useRef } from "react";

interface CheckInButtonProps {
  onChange: ({
    isChecked,
    isLoading,
  }: {
    isChecked: boolean;
    isLoading: boolean;
  }) => void;
  artistId: number;
  eventId: number;
  rewardId: number;
  winnerId: number;
  ticketCode: string | null;
  status?: TicketCheckInStatus;
  isTardy: boolean;
  disabled?: boolean;
}

export function CheckInButton({
  onChange,
  artistId,
  eventId,
  rewardId,
  winnerId,
  ticketCode,
  status,
  isTardy,
  disabled = false,
}: CheckInButtonProps) {
  const { t } = useTranslation();
  const { checkIn, cancelCheckIn } = useCheckIn({
    artistId: artistId,
    eventId: eventId,
    rewardId: rewardId,
    ticketCode: ticketCode,
    winnerId: winnerId,
  });
  const cancelRef = useRef<HTMLDialogElement>(null);

  const checkInBtnHandler = async () => {
    if (status === TicketCheckInStatus.NOT_CHECKED_IN) {
      onChange({ isChecked: true, isLoading: true });
      const success = !!(await checkIn({ isTardy }));
      onChange({ isChecked: success, isLoading: false });
    } else if (status === TicketCheckInStatus.CHECKED_IN) {
      cancelRef.current?.showModal();
    }
  };

  const color =
    status === TicketCheckInStatus.NOT_CHECKED_IN
      ? ""
      : status === TicketCheckInStatus.CHECKED_IN
        ? "btn-error"
        : "btn-default";
  const label =
    status === TicketCheckInStatus.NOT_CHECKED_IN
      ? t("userdetail_checkin_submit")
      : status === TicketCheckInStatus.CHECKED_IN
        ? t("userdetail_checkin_cancel")
        : "";

  return (
    <>
      <Button
        className={twclsx(
          color,
          "w-full rounded-none border-0 h-12",
          disabled && "btn-disabled",
        )}
        onClick={checkInBtnHandler}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.17071 4C8.58254 2.83481 9.69378 2 11 2H13C14.3062 2 15.4175 2.83481 15.8293 4H17C18.6569 4 20 5.34315 20 7V19C20 20.6569 18.6569 22 17 22H7C5.34315 22 4 20.6569 4 19V7C4 5.34315 5.34315 4 7 4H8.17071ZM8.17071 6H7C6.44772 6 6 6.44772 6 7V19C6 19.5523 6.44772 20 7 20H17C17.5523 20 18 19.5523 18 19V7C18 6.44772 17.5523 6 17 6H15.8293C15.4175 7.16519 14.3062 8 13 8H11C9.69378 8 8.58254 7.16519 8.17071 6ZM11 4C10.4477 4 10 4.44772 10 5C10 5.55228 10.4477 6 11 6H13C13.5523 6 14 5.55228 14 5C14 4.44772 13.5523 4 13 4H11ZM15.7071 11.2929C16.0976 11.6834 16.0976 12.3166 15.7071 12.7071L11.7071 16.7071C11.3166 17.0976 10.6834 17.0976 10.2929 16.7071L8.29289 14.7071C7.90237 14.3166 7.90237 13.6834 8.29289 13.2929C8.68342 12.9024 9.31658 12.9024 9.70711 13.2929L11 14.5858L14.2929 11.2929C14.6834 10.9024 15.3166 10.9024 15.7071 11.2929Z"
          />
        </svg>
        <p>{label}</p>
      </Button>
      <Dialog ref={cancelRef}>
        <DialogMessage>{t("userdetail_dialog_cancel")}</DialogMessage>
        <DialogActions>
          <div className={"flex gap-2 justify-end w-full"}>
            <button className={"btn basis-1/4"}>
              {t("common_dialog_cancel")}
            </button>
            <Button
              className={"btn btn-primary basis-1/4"}
              onClick={async () => {
                onChange({ isChecked: false, isLoading: true });
                await cancelCheckIn();
                onChange({ isChecked: false, isLoading: false });
              }}
            >
              {t("common_dialog_yes")}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
}
